"use client";

import { useState } from "react";

import { logError } from "@/lib/logError";
import { useCustomerAccount } from "@/providers";
import { getCompanyConfig } from "@/utils/companyConfig";
import { downloadUri } from "@/utils/downloadFile";
import { runWithCleanup } from "@/utils/runWithCleanup";

import type { PdfBlockchainSettlementInstructionProps } from "./PdfBlockchainSettlementInstruction";
import type { PdfFiatSettlementInstructionProps } from "./PdfFiatSettlementInstruction";

type FiatDownloadSettlementInstructionProps =
  PdfFiatSettlementInstructionProps & {
    type: "currencies";
  };

type BlockchainDownloadSettlementInstructionProps =
  PdfBlockchainSettlementInstructionProps & {
    type: "digital-assets";
  };

type UseDownloadSettlementInstructionPdfProps =
  | FiatDownloadSettlementInstructionProps
  | BlockchainDownloadSettlementInstructionProps;

/**
 * Builds the exact props object handed to <PdfFiatSettlementInstruction>.
 * Extracted (and spread-based) because the previous hand-written prop
 * allowlist silently dropped `subtitle` — a template prop the hook's type
 * accepted but never forwarded. Pure, so the forwarding contract is
 * unit-testable without rendering a PDF.
 */
export function buildFiatPdfElementProps(
  props: FiatDownloadSettlementInstructionProps,
  company: { name: string; address: string[]; footerText: string[] }
): PdfFiatSettlementInstructionProps {
  const { type: _type, ...templateProps } = props;
  void _type;
  return {
    ...templateProps,
    companyName: company.name,
    companyAddress: company.address,
    footerText: company.footerText,
  };
}

const downloadPdf = async (
  props: UseDownloadSettlementInstructionPdfProps,
  company: { name: string; address: string[]; footerText: string[] }
) => {
  const { type } = props;

  const { pdf } = await import("@react-pdf/renderer");

  let blob: Blob;

  if (type === "currencies") {
    const { default: PdfFiatDocument } =
      await import("./PdfFiatSettlementInstruction");

    const elementProps = buildFiatPdfElementProps(
      props as FiatDownloadSettlementInstructionProps,
      company
    );
    blob = await pdf(<PdfFiatDocument {...elementProps} />).toBlob();

    const url = URL.createObjectURL(blob);
    downloadUri(url, `${elementProps.currency} deposit details.pdf`);

    return;
  } else {
    const { default: PdfBlockchainDocument } =
      await import("./PdfBlockchainSettlementInstruction");

    const {
      title,
      currency,
      walletTitle,
      walletAddress,
      qrImage,
      qrTitle,
      warningText,
      networkTitle,
      networkName,
      explainers,
      explainersHeading,
    } = props as BlockchainDownloadSettlementInstructionProps;

    blob = await pdf(
      <PdfBlockchainDocument
        currency={currency}
        title={title}
        walletTitle={walletTitle}
        walletAddress={walletAddress}
        qrImage={qrImage}
        qrTitle={qrTitle}
        warningText={warningText}
        networkTitle={networkTitle}
        networkName={networkName}
        explainers={explainers}
        explainersHeading={explainersHeading}
        footerText={company.footerText}
        companyName={company.name}
        companyAddress={company.address}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    downloadUri(url, `${networkName} deposit details.pdf`);
  }
};

const useDownloadSettlementInstructionPdf = (
  data: UseDownloadSettlementInstructionPdfProps
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { customerAccount } = useCustomerAccount();

  // Booking-entity-aware brand + legal footer, mirroring statement PDFs: fiat
  // resolves to Universe Payments (UP); digital assets resolve UT/UM from the
  // account's bookingEntityCodes.
  const company = getCompanyConfig(
    data.type,
    customerAccount?.customerAccount?.bookingEntityCodes
  );

  const downloadSettlementInstructionPdf = async () => {
    setIsError(false);
    setIsLoading(true);
    // runWithCleanup, not try/finally, keeps the React Compiler lint rules on
    // for this hook.
    await runWithCleanup(
      () =>
        downloadPdf(data, {
          name: company.name,
          address: company.address,
          footerText: company.footerText,
        }).catch((error: unknown) => {
          // Client-side PDF rendering — never an RTK error, so without this
          // logError a renderer regression (font load, data-shape crash) kills
          // the download for everyone with zero Sentry signal. Matches the
          // generateStatementPdf pattern.
          logError("Failed to generate settlement instructions PDF", error, {
            type: data.type,
          });
          setIsError(true);
        }),
      () => setIsLoading(false)
    );
  };

  return {
    isLoading,
    isError,
    downloadSettlementInstructionPdf,
  };
};

export default useDownloadSettlementInstructionPdf;
